import { Input, InputRef, Typography } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addCardProps } from '../../actions';
import { AnswerDetailsInput, AnswerOptionInput } from '../../graphql/api';
import { IState } from '../../graphql/types';
import { formInputLength, handleInputChange } from '../utils';
import { CardFormField } from './CardFormField';

import styles from './CardForm.module.css';

const defaultAnsDetails = {
  answerOptions: [],
  userAnswer: null,
};

const PollForm: React.FC<{ validationFailed: boolean; saving: boolean }> = ({
  validationFailed,
  saving,
}) => {
  const dispatch = useDispatch();
  const ref = React.createRef<InputRef>();
  const question = useSelector<IState, string>(
    state => state.cardProps?.question ?? ''
  );
  const answerDetails = useSelector<IState, AnswerDetailsInput>(
    state => state.cardProps?.answerDetails ?? defaultAnsDetails,
    () => false
  );
  const answerOptions = useSelector<IState, AnswerOptionInput[]>(
    state => state.cardProps?.answerDetails?.answerOptions ?? [],
    () => false
  );

  const updateItem = React.useCallback(
    index => e => {
      if (e.target.value) {
        const tmpAnsDetails = answerDetails;
        if (tmpAnsDetails.answerOptions?.[index]) {
          tmpAnsDetails.answerOptions[index].text = e.target.value;
        }
        dispatch(addCardProps('answerDetails', tmpAnsDetails));
      } else {
        const tmpAnsDetails = answerDetails;
        tmpAnsDetails.answerOptions?.splice(index, 1);
        tmpAnsDetails.answerOptions?.forEach((answer, i) => {
          // Reordering indices
          answer.index = i;
          answer.sortOrder = index;
        });
        dispatch(addCardProps('answerDetails', tmpAnsDetails));
      }
    },
    [dispatch, answerDetails]
  );

  const createItem = React.useCallback(
    (e, index) => {
      answerDetails.answerOptions = answerOptions;
      const tmpAnsDetails = answerDetails;
      const newAnswer = {
        index,
        sortOrder: index,
        text: e.target.value,
      };
      if (
        tmpAnsDetails.answerOptions &&
        tmpAnsDetails.answerOptions.length - 1 < index
      ) {
        tmpAnsDetails.answerOptions.push(newAnswer);
      } else {
        tmpAnsDetails.answerOptions![index] = newAnswer;
      }
      dispatch(addCardProps('answerDetails', tmpAnsDetails));
    },
    [dispatch, answerDetails, answerOptions]
  );

  return (
    <div className={styles.formWrapper}>
      <CardFormField
        boldLabel={true}
        name="quizQuestion"
        fieldLabel="Question"
        defaultValue={question}
        customValidator={e => formInputLength(e.target.value, 300)}
        errorName={'a question'}
        customRules={[
          { max: 300, message: 'Limit of 300 characters exceeded' },
        ]}
      >
        <Input
          autoFocus
          placeholder="Ask a question"
          className="modal-form-input"
          onChange={handleInputChange(value =>
            dispatch(addCardProps('question', value))
          )}
          disabled={saving}
        />
      </CardFormField>

      <div className={styles.pollFormEntries}>
        {answerOptions.map((item, index) =>
          answerOptions.length - 1 === index ? (
            <React.Fragment key={index}>
              <Typography className={styles.choiceLabelText}>
                Choice {index + 1}
                {index + 1 > 2 && ' (optional)'}
              </Typography>
              <Input
                autoFocus
                className="modal-form-input list-item-input"
                value={item.text}
                onChange={e => {
                  if (e.currentTarget.value.length <= 60) updateItem(index)(e);
                }}
                placeholder={index === 0 ? 'Yes' : index === 1 ? 'No' : 'Maybe'}
                disabled={saving}
              />
            </React.Fragment>
          ) : (
            <React.Fragment key={index}>
              <Typography className={styles.choiceLabelText}>
                Choice {index + 1}
                {index + 1 > 2 && ' (optional)'}
              </Typography>
              <Input
                className={`modal-form-input list-item-input ${
                  answerOptions.length < 2 && validationFailed
                    ? styles.notValidInput
                    : ''
                }`}
                value={item.text}
                placeholder={index === 0 ? 'Yes' : index === 1 ? 'No' : 'Maybe'}
                onChange={e => {
                  if (e.currentTarget.value.length <= 60) updateItem(index)(e);
                }}
                disabled={saving}
              />
            </React.Fragment>
          )
        )}
        <>
          {[...Array(4)].map((_, idx) => (
            <>
              {answerOptions[idx] ? undefined : idx < 3 ||
                (idx === 3 && answerOptions[idx - 2]) ? (
                <>
                  <Typography className={styles.choiceLabelText}>
                    {idx < 2
                      ? `Choice ${idx + 1}`
                      : answerOptions[idx - 1]
                      ? `Choice ${idx + 1} (optional)`
                      : `Add choice (optional)`}
                  </Typography>
                  <Input
                    ref={ref}
                    disabled={(!(idx < 2) && !answerOptions[idx - 1]) || saving}
                    className={`modal-form-input list-item-input ${
                      answerOptions.length < 2 && validationFailed
                        ? styles.notValidInput
                        : ''
                    }`}
                    value={''}
                    placeholder={idx === 0 ? 'Yes' : idx === 1 ? 'No' : 'Maybe'}
                    onChange={e => createItem(e, answerOptions.length)}
                  />
                </>
              ) : undefined}
            </>
          ))}
        </>
      </div>
    </div>
  );
};

export default PollForm;
